/**
 * "How to get the file" — the manual VIP iDIG export, step by step.
 *
 * VIP prohibits automated access to iDIG, so a person runs this export
 * by hand and uploads the result on Admin → Data uploads. The steps were
 * recorded on 2026-10-08 and mirror docs/depletions-sync.md in the sales
 * backend — keep the two in step.
 *
 * Shown as a timeline: four phases (sign in, time periods, columns,
 * export) on one gold rail, the ten steps numbered along it. Collapsed
 * until someone opens it — regulars don't need it in the way.
 *
 * The month count (step 5) is the one people get wrong: a short count
 * silently drops the oldest months (the Oct 6 export lost Jan 2024), and
 * the upload check then refuses the file. So the step carries a small
 * calculator instead of a formula.
 */

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  ButtonBase,
  Collapse,
  Dialog,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState, type ComponentType, type ReactNode } from 'react';

import { colors, uiType } from '../../theme';

const INK = '#0f0a07';
const IVORY = '#fdf9f0';
const GOLD = colors.goldDark;
const RULE = 'rgba(15, 10, 7, 0.08)';
const MUTED = 'rgba(15, 10, 7, 0.6)';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

const VIP_URL = 'https://reports.vtinfo.com/';
const START_YEAR = 2024;
const OPEN_KEY = 'hy.dataUploads.guideOpen';

/** Read by screen readers, not shown. */
const visuallyHidden = {
  position: 'absolute',
  top: 0,
  left: 0,
  margin: -1,
  padding: 0,
  border: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** Month columns for a full-history report: Jan 2024 through `month` (YYYY-MM). */
function monthColumns(month: string): number | null {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return null;
  const n = (y - START_YEAR) * 12 + m;
  return n > 0 ? n : null;
}

function thisMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthName(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y ?? START_YEAR, (m ?? 1) - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

/** A UI label exactly as it reads in iDIG. */
function Ui({ children }: { children: ReactNode }) {
  return (
    <Box component="strong" sx={{ fontWeight: 700, color: INK }}>
      {children}
    </Box>
  );
}

/**
 * A picture of the iDIG control to use — a selected radio or a ticked
 * checkbox — with a soft pulse so it reads as "this one". Not a real
 * input: nothing here is clickable, it only shows what to click in VIP.
 * The pulse stops for anyone who prefers reduced motion.
 */
function VipControl({ kind, label }: { kind: 'radio' | 'checkbox'; label: string }) {
  const radio = kind === 'radio';
  return (
    <Box
      role="img"
      aria-label={`In VIP: ${radio ? 'select' : 'tick'} "${label}"`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.25,
        maxWidth: '100%',
        my: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        bgcolor: '#fff',
        border: `1px solid rgba(187, 140, 63, 0.45)`,
        boxShadow: '0 1px 6px rgba(142, 106, 42, 0.12)',
        '@keyframes vipPulse': {
          '0%': { transform: 'scale(1)', opacity: 0.55 },
          '70%': { transform: 'scale(2.1)', opacity: 0 },
          '100%': { transform: 'scale(2.1)', opacity: 0 },
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          flexShrink: 0,
          width: 18,
          height: 18,
          borderRadius: radio ? '50%' : '4px',
          border: `2px solid ${GOLD}`,
          bgcolor: radio ? '#fff' : GOLD,
          display: 'grid',
          placeItems: 'center',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: -2,
            borderRadius: radio ? '50%' : '5px',
            bgcolor: GOLD,
            animation: 'vipPulse 1.8s ease-out infinite',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
          },
        }}
      >
        {radio ? (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: GOLD }} />
        ) : (
          <CheckRoundedIcon sx={{ fontSize: 14, color: '#fff' }} />
        )}
      </Box>
      <Typography
        component="span"
        sx={{ fontFamily: FONT_INTER, fontSize: uiType.body, fontWeight: 600, color: INK }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/** The iDIG dimensions to add in step 7. Product must stay the last one. */
const DIMENSIONS = [
  'Dist State',
  'OnOff Premise',
  'Account Name',
  'Acct Zip',
  'Acct County',
  'Product',
] as const;

function DimensionList() {
  return (
    <Box
      component="ul"
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        my: 1,
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: 0.75,
      }}
    >
      {DIMENSIONS.map((d) => {
        const last = d === 'Product';
        return (
          <Box
            component="li"
            key={d}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: 1.25,
              bgcolor: last ? IVORY : 'rgba(15, 10, 7, 0.025)',
              border: `1px solid ${last ? 'rgba(187, 140, 63, 0.4)' : RULE}`,
              minWidth: 0,
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 18,
                height: 18,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(187, 140, 63, 0.15)',
                color: GOLD,
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 13 }} />
            </Box>
            <Typography
              component="span"
              sx={{ fontFamily: FONT_INTER, fontSize: uiType.body, fontWeight: 600, color: INK }}
            >
              {d}
            </Typography>
            {last && (
              <Box
                component="span"
                sx={{
                  ml: 'auto',
                  flexShrink: 0,
                  px: 0.75,
                  py: 0.1,
                  borderRadius: 999,
                  fontFamily: FONT_INTER,
                  fontSize: uiType.micro,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: GOLD,
                  bgcolor: 'rgba(187, 140, 63, 0.12)',
                }}
              >
                Keep last
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * A "See it in VIP" link that opens a screenshot of the iDIG screen in a
 * viewer — kept out of the step until asked for, so the timeline stays
 * short for people who already know the screen.
 */
function ScreenshotHint({
  src,
  alt,
  caption,
  label = 'See it in VIP',
}: {
  src: string;
  alt: string;
  caption: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
  return (
    <>
      <ButtonBase
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          mt: 1,
          px: 1.25,
          py: 0.6,
          borderRadius: 999,
          border: `1px dashed rgba(187, 140, 63, 0.6)`,
          bgcolor: IVORY,
          color: GOLD,
          fontFamily: FONT_INTER,
          fontSize: uiType.bodySm,
          fontWeight: 700,
          transition: 'background-color 140ms ease, border-color 140ms ease',
          '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.1)', borderColor: GOLD },
          '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
        }}
      >
        <ImageOutlinedIcon sx={{ fontSize: 17 }} />
        {label}
      </ButtonBase>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen={fullScreen}
        maxWidth="lg"
        fullWidth
        aria-label={alt}
        PaperProps={{ sx: { bgcolor: IVORY, borderTop: `3px solid ${GOLD}` } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderBottom: `1px solid ${RULE}` }}
        >
          <Typography
            sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: MUTED, minWidth: 0 }}
          >
            {caption}
          </Typography>
          <IconButton aria-label="Close" onClick={() => setOpen(false)} sx={{ flexShrink: 0 }}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Box sx={{ p: { xs: 1.5, sm: 3 }, overflow: 'auto' }}>
          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
              display: 'block',
              mx: 'auto',
              maxWidth: '100%',
              // Fit the whole screen in view on a computer; "Open full size" for detail.
              maxHeight: { sm: 'calc(100vh - 200px)' },
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: 1.5,
              border: `1px solid ${RULE}`,
              boxShadow: '0 4px 24px rgba(15, 10, 7, 0.12)',
              bgcolor: '#fff',
            }}
          />
          <Typography
            sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED, mt: 1.5 }}
          >
            <Link href={src} target="_blank" rel="noopener noreferrer" sx={{ color: GOLD }}>
              Open full size
            </Link>
          </Typography>
        </Box>
      </Dialog>
    </>
  );
}

function MonthCalculator() {
  const [month, setMonth] = useState(thisMonth);
  const n = monthColumns(month);
  return (
    <Box
      sx={{
        mt: 1.25,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: '#fff',
        border: `1px solid rgba(187, 140, 63, 0.35)`,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <TextField
        size="small"
        type="month"
        label="Through month"
        value={month}
        onChange={(e) => setMonth(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: `${START_YEAR}-01` }}
        sx={{ width: { xs: '100%', sm: 190 }, '& input': { fontFamily: FONT_INTER } }}
      />
      <Typography
        aria-live="polite"
        sx={{ fontFamily: FONT_INTER, fontSize: uiType.body, color: INK, minWidth: 0 }}
      >
        {n ? (
          <>
            Enter{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: 18,
                color: GOLD,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {n}
            </Box>{' '}
            month columns (Jan {START_YEAR} – {monthName(month)})
          </>
        ) : (
          `Pick a month from Jan ${START_YEAR} onwards.`
        )}
      </Typography>
    </Box>
  );
}

interface Step {
  where: string;
  body: ReactNode;
}

interface Phase {
  title: string;
  Icon: ComponentType<{ sx?: object }>;
  steps: Step[];
}

const PHASES: Phase[] = [
  {
    title: 'Sign in to VIP',
    Icon: LoginRoundedIcon,
    steps: [
      {
        where: 'VIP sign-in',
        body: (
          <>
            Open{' '}
            <Link href={VIP_URL} target="_blank" rel="noopener noreferrer" sx={{ color: GOLD }}>
              reports.vtinfo.com
              <OpenInNewRoundedIcon sx={{ fontSize: 13, ml: 0.25, verticalAlign: '-2px' }} />
            </Link>{' '}
            and sign in: email → <Ui>Next</Ui> → password → <Ui>Sign in</Ui>.
          </>
        ),
      },
      {
        where: 'Any page',
        body: (
          <>
            Click the <Ui>iDIG logo</Ui> (top left) to open the Report Builder.
          </>
        ),
      },
    ],
  },
  {
    title: 'Set up the time periods',
    Icon: CalendarMonthOutlinedIcon,
    steps: [
      {
        where: 'Report Builder → Sales Reports',
        body: (
          <>
            Choose <Ui>Rolling Periods</Ui>.
          </>
        ),
      },
      {
        where: 'Set Up Your Time Periods',
        body: (
          <>
            Under <Ui>Through</Ui>, click <Ui>Options</Ui> and select:
            <Box>
              <VipControl kind="radio" label="the last day of sales" />
            </Box>
            The Through date updates to VIP's newest day (e.g. 10/12/2026).
          </>
        ),
      },
      {
        where: 'Set Up Your Time Periods',
        body: (
          <>
            <Ui>How many Month columns</Ui>: every month from Jan {START_YEAR} through the{' '}
            <Ui>Through</Ui> month — not today's month, which can differ early in a month. Then:
            Period <Ui>Month</Ui>, Use Depletion Period <Ui>off</Ui>, Methods{' '}
            <Ui>Actual Selling Day</Ui>, Measures <Ui>9 Liter Equivs</Ui> only.
            <MonthCalculator />
          </>
        ),
      },
      {
        where: 'Set Up Your Time Periods',
        body: (
          <>
            Check the preview's first column reads <Ui>1 Month 1/1/2024 thru 1/31/2024</Ui>, then{' '}
            <Ui>Continue</Ui>. If it starts later, the month count is too low.
          </>
        ),
      },
    ],
  },
  {
    title: 'Choose the columns',
    Icon: ViewColumnOutlinedIcon,
    steps: [
      {
        where: 'Customize Your Report',
        body: (
          <>
            Click <Ui>All Dimensions</Ui> and add these six (Brand is already on):
            <DimensionList />
            Then <Ui>Close</Ui>. Any order works, as long as <Ui>Product</Ui> is last.
            <Box>
              <ScreenshotHint
                src="/guides/vip-dimensions.png"
                alt="iDIG Customize Your Report: All Dimensions panel with Dist State, OnOff Premise, Account Name, Acct Zip, Acct County and Product selected"
                caption="iDIG → Customize Your Report → All Dimensions. The six dimensions to add are highlighted."
              />
            </Box>
          </>
        ),
      },
      {
        where: 'Customize Your Report',
        body: (
          <>
            The preview columns should read: Dist States, OnOff Premises, Account Names, Address,
            City, State, Distributor, Acct Zips, Acct Counties, Products. Then <Ui>View Report</Ui>.
          </>
        ),
      },
    ],
  },
  {
    title: 'Export and upload',
    Icon: FileDownloadOutlinedIcon,
    steps: [
      {
        where: 'Report Viewer',
        body: (
          <>
            Click <Ui>Export Report</Ui> → <Ui>Excel</Ui>, then tick:
            <Box>
              <VipControl kind="checkbox" label="Include Last Complete Day of Sales" />
            </Box>
            Then <Ui>Export</Ui>. You get <Ui>Rolling Periods.xlsx</Ui> (about 1 MB).
          </>
        ),
      },
      {
        where: 'This page',
        body: (
          <>
            Drop <Ui>Rolling Periods.xlsx</Ui> on the upload box below, review the checks and load
            it.
          </>
        ),
      },
    ],
  },
];

const STEP_COUNT = PHASES.reduce((n, p) => n + p.steps.length, 0);

// ── Timeline ─────────────────────────────────────────────────────

/** Width of the rail column; nodes centre on it. */
const RAIL = 40;

/**
 * One row of the timeline: a node on the rail, content beside it. The
 * rail line is drawn per row so it always spans the row's full height;
 * it starts at the first node's centre and stops at the last one's.
 */
function TimelineRow({
  node,
  first,
  last,
  children,
}: {
  node: ReactNode;
  first?: boolean;
  last?: boolean;
  children: ReactNode;
}) {
  const centre = 20; // node centre from the row's top
  return (
    <Box
      component="li"
      sx={{ display: 'grid', gridTemplateColumns: `${RAIL}px minmax(0, 1fr)`, columnGap: 1.75 }}
    >
      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: '50%',
            width: 2,
            ml: '-1px',
            top: first ? centre : 0,
            bottom: last ? 'auto' : 0,
            height: last ? centre : 'auto',
            background: 'linear-gradient(to bottom, rgba(187,140,63,0.45), rgba(187,140,63,0.25))',
            display: first && last ? 'none' : 'block',
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1 }}>{node}</Box>
      </Box>
      <Box sx={{ minWidth: 0, pb: last ? 0 : 2.5 }}>{children}</Box>
    </Box>
  );
}

function PhaseNode({ Icon }: { Icon: Phase['Icon'] }) {
  return (
    <Box
      sx={{
        width: RAIL,
        height: RAIL,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        color: '#fff',
        background: `linear-gradient(135deg, ${colors.goldLight} 0%, ${GOLD} 100%)`,
        boxShadow: '0 0 0 4px #fff, 0 2px 8px rgba(142, 106, 42, 0.25)',
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
    </Box>
  );
}

function StepNode({ n, final }: { n: number; final: boolean }) {
  return (
    <Box
      sx={{
        mt: '8px',
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        bgcolor: final ? INK : '#fff',
        color: final ? '#fff' : INK,
        border: `1.5px solid ${final ? INK : 'rgba(187, 140, 63, 0.6)'}`,
        boxShadow: '0 0 0 3px #fff',
        fontFamily: FONT_INTER,
        fontWeight: 700,
        fontSize: uiType.caption,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {n}
    </Box>
  );
}

function Timeline() {
  let n = 0;
  return (
    <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, minWidth: 0 }}>
      {PHASES.map((phase, pi) => (
        <Box component="li" key={phase.title} sx={{ listStyle: 'none' }}>
          <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            <TimelineRow node={<PhaseNode Icon={phase.Icon} />} first={pi === 0}>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: uiType.micro,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: GOLD,
                  pt: '4px',
                }}
              >
                Phase {pi + 1} of {PHASES.length}
              </Typography>
              <Typography
                component="h4"
                sx={{ fontFamily: FONT_INTER, fontWeight: 700, fontSize: 16, color: INK }}
              >
                {phase.title}
              </Typography>
            </TimelineRow>
            {phase.steps.map((step, si) => {
              n += 1;
              const final = n === STEP_COUNT;
              return (
                <TimelineRow
                  key={n}
                  node={<StepNode n={n} final={final} />}
                  last={pi === PHASES.length - 1 && si === phase.steps.length - 1}
                >
                  <Box
                    sx={{
                      mt: '6px',
                      p: { xs: 1.5, sm: 1.75 },
                      borderRadius: 1.5,
                      bgcolor: final ? IVORY : '#fff',
                      border: `1px solid ${final ? 'rgba(187, 140, 63, 0.35)' : RULE}`,
                      transition: 'border-color 160ms ease, box-shadow 160ms ease',
                      '&:hover': {
                        borderColor: 'rgba(187, 140, 63, 0.4)',
                        boxShadow: '0 2px 10px rgba(15, 10, 7, 0.05)',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_INTER,
                        fontSize: uiType.micro,
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: MUTED,
                      }}
                    >
                      <Box component="span" sx={visuallyHidden}>
                        Step {n}:{' '}
                      </Box>
                      {step.where}
                    </Typography>
                    <Typography
                      component="div"
                      sx={{
                        fontFamily: FONT_INTER,
                        fontSize: uiType.body,
                        lineHeight: 1.6,
                        color: 'rgba(15, 10, 7, 0.8)',
                        mt: 0.25,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {step.body}
                    </Typography>
                  </Box>
                </TimelineRow>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Side notes ───────────────────────────────────────────────────

function Note({
  Icon,
  tone,
  title,
  children,
}: {
  Icon: ComponentType<{ sx?: object }>;
  tone: 'info' | 'warn';
  title: string;
  children: ReactNode;
}) {
  const accent = tone === 'warn' ? colors.warning : GOLD;
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1.5,
        bgcolor: tone === 'warn' ? 'rgba(217, 119, 6, 0.06)' : IVORY,
        border: `1px solid ${tone === 'warn' ? 'rgba(217, 119, 6, 0.25)' : 'rgba(187, 140, 63, 0.25)'}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Icon sx={{ fontSize: 18, color: accent }} />
        <Typography
          sx={{ fontFamily: FONT_INTER, fontWeight: 700, fontSize: uiType.body, color: INK }}
        >
          {title}
        </Typography>
      </Stack>
      <Typography
        component="div"
        sx={{
          fontFamily: FONT_INTER,
          fontSize: uiType.bodySm,
          lineHeight: 1.6,
          color: 'rgba(15, 10, 7, 0.75)',
          mt: 0.75,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SideNotes() {
  return (
    <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 96 }, alignSelf: 'start' }}>
      <Note Icon={InfoOutlinedIcon} tone="info" title="Before you start">
        You need the VIP login for <Ui>Blue Ridge Spirits and Wine</Ui> (brand{' '}
        <Ui>Hooten &amp; Young</Ui>). The export takes about 2 minutes and produces{' '}
        <Ui>Rolling Periods.xlsx</Ui>.
      </Note>
      <Note Icon={WarningAmberRoundedIcon} tone="warn" title="Don't re-run a saved favorite">
        A favorite keeps its month count while the Through date moves forward, so each month the
        oldest month quietly drops off — and the upload refuses the file. Re-enter the count every
        time.
      </Note>
      <Note Icon={FileDownloadOutlinedIcon} tone="info" title="Nevada report">
        It isn't in VIP. The Nevada distributor sends it as an Excel file — upload it here
        unchanged.
      </Note>
    </Stack>
  );
}

// ── Shell ────────────────────────────────────────────────────────

function readOpen(): boolean {
  try {
    // Collapsed unless this person opened it last time.
    return window.localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

export function VipExportGuide() {
  const [open, setOpen] = useState(readOpen);
  const toggle = () => {
    setOpen((o) => {
      try {
        window.localStorage.setItem(OPEN_KEY, o ? '0' : '1');
      } catch {
        /* storage unavailable — the toggle still works for this visit */
      }
      return !o;
    });
  };

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${RULE}`,
        borderRadius: 2,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={toggle}
        aria-expanded={open}
        aria-controls="vip-export-guide"
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          textAlign: 'left',
          gap: 1.5,
          px: { xs: 2, md: 3 },
          py: { xs: 1.75, md: 2 },
          '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.04)' },
          '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: -2 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 1.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(187, 140, 63, 0.12)',
            color: GOLD,
          }}
        >
          <RouteOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontFamily: FONT_INTER, fontWeight: 700, fontSize: 16, color: INK }}>
            How to get the file from VIP
          </Typography>
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: MUTED }}>
            {PHASES.length} phases · {STEP_COUNT} steps · about 2 minutes
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {' '}
              — VIP doesn't allow automated exports, so this part is done by hand.
            </Box>
          </Typography>
        </Box>
        <Box
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline' },
            fontFamily: FONT_INTER,
            fontSize: uiType.bodySm,
            fontWeight: 700,
            color: GOLD,
            whiteSpace: 'nowrap',
          }}
        >
          {open ? 'Hide steps' : 'Show steps'}
        </Box>
        <ExpandMoreRoundedIcon
          sx={{
            color: GOLD,
            flexShrink: 0,
            transition: 'transform 180ms ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </ButtonBase>

      <Collapse in={open} id="vip-export-guide" unmountOnExit>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 2.5, md: 3 },
            borderTop: `1px solid ${RULE}`,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 320px' },
            gap: { xs: 3, lg: 5 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Timeline />
          </Box>
          <SideNotes />
        </Box>
      </Collapse>
    </Box>
  );
}
